import React, { useEffect, useState } from "react";
import { Alert, Table, Typography } from "antd";
import { getEmailStatsData } from "api/Api";

const { Title } = Typography;

type MetricRow = {
  key: string;
  metric: string;
  today: number;
  yesterday: number;
  dayPercentage: string;
  thisWeek: number;
  lastWeek: number;
  weekPercentage: string;
  thisMonth: number;
  lastMonth: number;
  monthPercentage: string;
  prevMonthMtd: number;
  mtdMonthPercentage: string;
  prevYearMtd: number;
  mtdYearPercentage: string;
};

type EmailStatsResponse = {
  overview: Omit<MetricRow, "key">[];
  byTags: Omit<MetricRow, "key">[];
};

const periodColumns = [
  {
    title: "Metric",
    dataIndex: "metric",
    key: "metric",
    width: 400,
  },
  {
    title: "Today",
    dataIndex: "today",
    key: "today",
  },
  {
    title: "Yesterday",
    dataIndex: "yesterday",
    key: "yesterday",
  },
  {
    title: "Day %",
    dataIndex: "dayPercentage",
    key: "dayPercentage",
  },
  {
    title: "This Week",
    dataIndex: "thisWeek",
    key: "thisWeek",
  },
  {
    title: "Last Week",
    dataIndex: "lastWeek",
    key: "lastWeek",
  },
  {
    title: "Week %",
    dataIndex: "weekPercentage",
    key: "weekPercentage",
  },
  {
    title: "This Month",
    dataIndex: "thisMonth",
    key: "thisMonth",
  },
  {
    title: "Last Month",
    dataIndex: "lastMonth",
    key: "lastMonth",
  },
  {
    title: "Month %",
    dataIndex: "monthPercentage",
    key: "monthPercentage",
  },
  {
    title: "Prev Month MTD",
    dataIndex: "prevMonthMtd",
    key: "prevMonthMtd",
  },
  {
    title: "MTD %",
    dataIndex: "mtdMonthPercentage",
    key: "mtdMonthPercentage",
  },
  {
    title: "Prev Year MTD",
    dataIndex: "prevYearMtd",
    key: "prevYearMtd",
  },
  {
    title: "Year MTD %",
    dataIndex: "mtdYearPercentage",
    key: "mtdYearPercentage",
  },
];

const withKeys = (rows: Omit<MetricRow, "key">[] = []): MetricRow[] =>
  rows.map((row, index) => ({
    ...row,
    key: `${row.metric}-${index}`,
  }));

const EmailStats = () => {
  const [overviewRows, setOverviewRows] = useState<MetricRow[]>([]);
  const [tagRows, setTagRows] = useState<MetricRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const response: EmailStatsResponse = await getEmailStatsData();
      setOverviewRows(withKeys(response?.overview));
      setTagRows(withKeys(response?.byTags));
    } catch (err) {
      console.error("Failed to load email stats:", err);
      setOverviewRows([]);
      setTagRows([]);
      setError("Failed to load email statistics from the API.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div>
      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Email stats unavailable"
          description={error}
        />
      ) : null}

      <Title level={5} style={{ marginTop: 0 }}>
        Email activity
      </Title>
      <Table
        loading={loading}
        dataSource={overviewRows}
        columns={periodColumns}
        scroll={{ x: "max-content" }}
        tableLayout="fixed"
        pagination={false}
        bordered
      />

      <Title level={5} style={{ marginTop: 24 }}>
        Sent by tags
      </Title>
      <Table
        loading={loading}
        dataSource={tagRows}
        columns={periodColumns}
        scroll={{ x: "max-content" }}
        tableLayout="fixed"
        pagination={false}
        bordered
        locale={{
          emptyText: loading ? "Loading..." : "No tagged emails configured",
        }}
      />
    </div>
  );
};

export default EmailStats;
